import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventsApi } from '../services/eventsApi';
import { staysApi } from '../services/staysApi';
import { getVisibleDotIndices } from '../utils/carouselDots';
import { FilterSectionBoneIcon } from './FilterSectionBoneIcon';
import { eventTicketsCtaLabel, isContactToBookEvent, isRazorpayEmbedEvent, navigateEventContactBooking, openRazorpayBooking } from '../utils/eventBookingConfig';

/** @typedef {'all' | 'stays' | 'events'} SpotlightMode */

const SpotlightCarousel = ({ slides = null, mode = 'all' }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  const containerRef = useRef(null);
  // Data fetching effect
  useEffect(() => {
    if (slides && slides.length > 0) {
      setCarouselSlides(slides);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSpotlightData = async () => {
      try {
        setLoading(true);
        setCurrentSlide(0);

        const fetchEvents = mode === 'all' || mode === 'events';
        const fetchStays = mode === 'all' || mode === 'stays';

        const [eventsRaw, staysRaw] = await Promise.all([
          fetchEvents ? eventsApi.getSpotlightEvents().catch(() => []) : Promise.resolve([]),
          fetchStays ? staysApi.getSpotlightStays().catch(() => []) : Promise.resolve([]),
        ]);

        if (cancelled) return;

        const events = eventsRaw || [];
        const stays = staysRaw || [];

        // Transform events data for carousel
        const eventSlides = events.map(event => ({
          id: event.eventId,
          eventId: event.eventId,
          type: 'event',
          title: event.title,
          description: event.description || '',
          image: event.images?.[0] || 'https://images.unsplash.com/photo-1552053831-71594a27632d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
          city: event.city,
          state: event.state,
          venue: event.venue,
          category: event.category,
          rating: event.rating,
          pricing: event.pricing,
          tags: event.tags || [],
          bookingMode: event.bookingMode || null,
        }));

        // Transform stays data for carousel
        const staySlides = stays.map(stay => ({
          id: stay.stayId,
          type: 'stay',
          title: stay.title,
          date: 'Available Now',
          image: stay.images?.[0] || 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
          city: stay.city,
          state: stay.state,
          rating: stay.rating,
          ratingTag: stay.ratingTag,
          founderVerified: stay.founderVerified
        }));

        let nextSlides = [];
        if (mode === 'all') {
          nextSlides = shuffleArray([...eventSlides, ...staySlides]);
        } else if (mode === 'events') {
          nextSlides = eventSlides;
        } else {
          nextSlides = staySlides;
        }

        if (nextSlides.length === 0) {
          setCarouselSlides([]);
        } else {
          setCarouselSlides(nextSlides);
        }
      } catch (error) {
        console.error('Error fetching spotlight data:', error);
        if (!cancelled) {
          setCarouselSlides([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSpotlightData();
    return () => {
      cancelled = true;
    };
  }, [slides, mode]);

  // Auto-advance carousel
  useEffect(() => {
    if (carouselSlides.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, [carouselSlides.length]);

  // Helper functions
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const goToSlide = (index) => {
    if (isAnimating || carouselSlides.length === 0) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const nextSlide = () => {
    if (isAnimating || carouselSlides.length === 0) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevSlide = () => {
    if (isAnimating || carouselSlides.length === 0) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    // If swipe distance is more than 50px
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swiped left - go to next slide
        nextSlide();
      } else {
        // Swiped right - go to previous slide  
        prevSlide();
      }
    }
  };

  // Handle button click based on item type
  const handleButtonClick = (slide) => {
    if (slide.type === 'event' && isRazorpayEmbedEvent(slide)) {
      openRazorpayBooking(slide);
      return;
    }
    if (slide.type === 'event' && isContactToBookEvent(slide)) {
      navigateEventContactBooking(navigate, slide);
      return;
    }
    if (slide.type === 'event') {
      // Navigate to event detail page
      navigate(`/event/${slide.id}`);
    } else if (slide.type === 'stay') {
      // Navigate to stay detail page
      navigate(`/property/${slide.id}`);
    } else {
      // Fallback for legacy data without type
      if (slide.eventId) {
        navigate(`/event/${slide.eventId}`);
      } else if (slide.stayId) {
        navigate(`/property/${slide.stayId}`);
      } else {
        // Default to event if no clear identifier
        navigate(`/event/${slide.id}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="relative">
        {/* Section Header */}
        <div className="flex items-center mb-[20px]">
          <h2 className="text-[20px] font-medium text-black">In the Spotlight</h2>
          <span className="text-2xl flex items-center">
            {/* Heart Icon */}
            <svg xmlns="http://www.w3.org/2000/svg" width="31" height="34" viewBox="0 0 31 34" fill="none" className="mr-2">
              <path d="M12.9866 8.04256C13.8758 8.16678 14.7258 8.48896 15.4739 8.98536C16.222 9.48177 16.8491 10.1397 17.3091 10.9108L17.3344 10.9534L17.3759 10.9351C18.1574 10.5929 19.0028 10.4213 19.8558 10.4317C20.7089 10.4422 21.5499 10.6344 22.3227 10.9955L22.5463 11.1043C23.5068 11.6026 24.3147 12.3514 24.8844 13.2713C25.4542 14.1913 25.7645 15.2482 25.7826 16.3301C25.8006 17.4121 25.5258 18.4787 24.9871 19.4172C24.4484 20.3557 23.666 21.131 22.7226 21.6611L22.4951 21.7829L22.4369 21.8075L13.1026 26.5848C12.8872 26.6949 12.6392 26.7233 12.4046 26.6645C12.1699 26.6058 11.9645 26.464 11.8264 26.2655L11.7616 26.1588L6.86717 16.8167C6.34446 15.8403 6.1022 14.7383 6.16722 13.6327C6.23224 12.5271 6.602 11.461 7.23556 10.5526C7.86912 9.6442 8.74175 8.92887 9.7568 8.48585C10.7719 8.04283 11.8897 7.8894 12.9866 8.04256Z" fill="black"/>
              <path d="M21.7437 0.741731C22.1579 0.799588 22.5538 0.949654 22.9022 1.18087C23.2507 1.41208 23.5428 1.71854 23.757 2.07767L23.7688 2.09753L23.7881 2.08901C24.1521 1.92963 24.5459 1.8497 24.9432 1.85456C25.3406 1.85941 25.7323 1.94894 26.0923 2.11716L26.1964 2.16783C26.6438 2.39992 27.0201 2.74868 27.2855 3.17717C27.5508 3.60567 27.6954 4.09795 27.7038 4.60189C27.7122 5.10583 27.5842 5.60266 27.3333 6.03978C27.0824 6.4769 26.7179 6.83803 26.2785 7.08493L26.1725 7.14168L26.1454 7.15311L21.7977 9.37828C21.6974 9.42957 21.5819 9.44277 21.4726 9.41542C21.3633 9.38807 21.2676 9.32203 21.2033 9.22954L21.1731 9.17988L18.8934 4.82853C18.65 4.37374 18.5371 3.86044 18.5674 3.34547C18.5977 2.8305 18.7699 2.33397 19.065 1.91085C19.3601 1.48773 19.7666 1.15455 20.2393 0.948204C20.7121 0.741856 21.2328 0.670393 21.7437 0.741731Z" fill="black"/>
            </svg>
            {/* Bone Icon */}
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
              <g clipPath="url(#clip0_250_22352)">
                <path d="M15.1395 3.06973C15.9352 3.06973 16.6982 3.3858 17.2608 3.94841C17.8234 4.51102 18.1395 5.27408 18.1395 6.06973C18.6328 6.06963 19.1186 6.19119 19.5537 6.42365C19.9889 6.65611 20.36 6.99229 20.6341 7.40242C20.9083 7.81255 21.0771 8.28397 21.1256 8.77492C21.1741 9.26588 21.1008 9.76121 20.9121 10.217C20.7234 10.6729 20.4252 11.0752 20.044 11.3882C19.6627 11.7013 19.2101 11.9155 18.7263 12.0119C18.2424 12.1083 17.7423 12.0839 17.2702 11.9408C16.798 11.7978 16.3685 11.5405 16.0195 11.1917L11.2615 15.9497C11.6049 16.2934 11.8597 16.7153 12.004 17.1793C12.1482 17.6432 12.1777 18.1352 12.0898 18.613C12.0019 19.0908 11.7992 19.5401 11.4993 19.9223C11.1994 20.3045 10.8111 20.6081 10.3679 20.8071C9.92466 21.006 9.43975 21.0944 8.95484 21.0645C8.46993 21.0346 7.99953 20.8874 7.58408 20.6356C7.16863 20.3837 6.82056 20.0347 6.5698 19.6186C6.31904 19.2025 6.17309 18.7317 6.1445 18.2467V18.0697H5.9685C5.48333 18.0413 5.01234 17.8954 4.59604 17.6447C4.17973 17.3939 3.83059 17.0457 3.57863 16.6301C3.32668 16.2146 3.17946 15.744 3.14965 15.2589C3.11983 14.7738 3.20832 14.2888 3.40748 13.8454C3.60665 13.4021 3.91053 13.0138 4.293 12.714C4.67546 12.4141 5.12505 12.2117 5.60308 12.124C6.08111 12.0364 6.57327 12.0662 7.03723 12.2109C7.50118 12.3556 7.92304 12.6109 8.2665 12.9547L13.0245 8.19673C12.6048 7.77729 12.3189 7.2428 12.203 6.66087C12.087 6.07893 12.1463 5.47569 12.3732 4.92742C12.6001 4.37916 12.9845 3.9105 13.4778 3.58072C13.9711 3.25095 14.5511 3.07486 15.1445 3.07473L15.1395 3.06973Z" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_250_22352">
                  <rect width="24" height="24" fill="white" transform="translate(0.139496 0.0697327)"/>
                </clipPath>
              </defs>
            </svg>
          </span>
        </div>
        
        {/* Loading skeleton */}
        <div className="flex gap-[16px]">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex-shrink-0 w-[100%] rounded-[24px] overflow-hidden">
              <div className="relative h-[524px] md:h-[500px] bg-gray-200 animate-pulse">
                <div className="absolute bottom-[4px] right-[4px] md:bottom-[8px] md:right-[8px] bg-white rounded-[20px] p-[12px] md:p-[16px] md:max-w-sm w-[calc(100%-8px)]">
                  <div className="h-6 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded mb-3 animate-pulse"></div>
                  <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (carouselSlides.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      {/* Section Header */}
      <div className="flex items-center mb-[20px]">
        <h2 className="text-[20px] font-medium text-black ">In the Spotlight</h2>
        <span className="text-2xl flex items-center">
          {/* Heart Icon */}
          <svg xmlns="http://www.w3.org/2000/svg" width="31" height="34" viewBox="0 0 31 34" fill="none" className="mr-2">
            <path d="M12.9866 8.04256C13.8758 8.16678 14.7258 8.48896 15.4739 8.98536C16.222 9.48177 16.8491 10.1397 17.3091 10.9108L17.3344 10.9534L17.3759 10.9351C18.1574 10.5929 19.0028 10.4213 19.8558 10.4317C20.7089 10.4422 21.5499 10.6344 22.3227 10.9955L22.5463 11.1043C23.5068 11.6026 24.3147 12.3514 24.8844 13.2713C25.4542 14.1913 25.7645 15.2482 25.7826 16.3301C25.8006 17.4121 25.5258 18.4787 24.9871 19.4172C24.4484 20.3557 23.666 21.131 22.7226 21.6611L22.4951 21.7829L22.4369 21.8075L13.1026 26.5848C12.8872 26.6949 12.6392 26.7233 12.4046 26.6645C12.1699 26.6058 11.9645 26.464 11.8264 26.2655L11.7616 26.1588L6.86717 16.8167C6.34446 15.8403 6.1022 14.7383 6.16722 13.6327C6.23224 12.5271 6.602 11.461 7.23556 10.5526C7.86912 9.6442 8.74175 8.92887 9.7568 8.48585C10.7719 8.04283 11.8897 7.8894 12.9866 8.04256Z" fill="black"/>
            <path d="M21.7437 0.741731C22.1579 0.799588 22.5538 0.949654 22.9022 1.18087C23.2507 1.41208 23.5428 1.71854 23.757 2.07767L23.7688 2.09753L23.7881 2.08901C24.1521 1.92963 24.5459 1.8497 24.9432 1.85456C25.3406 1.85941 25.7323 1.94894 26.0923 2.11716L26.1964 2.16783C26.6438 2.39992 27.0201 2.74868 27.2855 3.17717C27.5508 3.60567 27.6954 4.09795 27.7038 4.60189C27.7122 5.10583 27.5842 5.60266 27.3333 6.03978C27.0824 6.4769 26.7179 6.83803 26.2785 7.08493L26.1725 7.14168L26.1454 7.15311L21.7977 9.37828C21.6974 9.42957 21.5819 9.44277 21.4726 9.41542C21.3633 9.38807 21.2676 9.32203 21.2033 9.22954L21.1731 9.17988L18.8934 4.82853C18.65 4.37374 18.5371 3.86044 18.5674 3.34547C18.5977 2.8305 18.7699 2.33397 19.065 1.91085C19.3601 1.48773 19.7666 1.15455 20.2393 0.948204C20.7121 0.741856 21.2328 0.670393 21.7437 0.741731Z" fill="black"/>
          </svg>
          <FilterSectionBoneIcon className="shrink-0 text-black" size={25} />
        </span>
      </div>

      {/* Horizontal Carousel Container */}
      <div 
        ref={containerRef}
        className="relative overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="flex gap-[16px] transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(calc(-${currentSlide * 100}% - ${currentSlide * 16}px))`
          }}
        >
          {carouselSlides.map((slide, index) => (
            <div 
              key={index}
              className="flex-shrink-0 w-[100%]  rounded-[24px] overflow-hidden "
            >
              <div className="relative h-[524px] md:h-[500px]">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url('${slide.image}')`
                  }}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                </div>



                {/* Content Overlay */}
                <div className="absolute bottom-[4px] right-[4px] md:bottom-[8px] md:right-[8px] bg-white rounded-[20px] p-[12px] md:p-[16px] md:max-w-sm w-[calc(100%-8px)]">
                  
                  <h3 className="font-medium text-[18px] text-black mb-[10px]">{slide.title}</h3>
                  {slide.type === 'stay' && slide.date && (
                    <div className="flex items-center mb-[12px]">
                      <span className="text-[13px] text-[#FF5E4C] mr-2">{slide.date}</span>
                    </div>
                  )}
                  
                  
                  <button 
                    onClick={() => handleButtonClick(slide)}
                    className="w-full relative overflow-hidden bg-black text-white p-[18px] rounded-[14px] text-[16px] font-medium hover:bg-gray-800 transition-colors flex items-center justify-center"
                  >
                    <span>
                      {slide.type === 'stay' ? 'Book Stay' : eventTicketsCtaLabel(slide)}
                    </span>
                    <svg className='absolute right-[10%] bottom-[-5%]' xmlns="http://www.w3.org/2000/svg" width="61" height="36" viewBox="0 0 61 36" fill="none">
                      <path d="M17.8291 8.93766C17.4553 8.93766 17.0741 8.84601 16.7368 8.76537C15.8388 8.5491 13.7384 8.04692 14.2406 5.94657C14.4348 5.13283 14.8784 4.42354 15.4887 3.94885C16.1375 3.44301 16.9274 3.25424 17.6623 3.43019C19.1322 3.78208 19.99 5.48471 19.5739 7.22401C19.244 8.60224 18.5476 8.93766 17.8291 8.93766ZM17.1107 4.83226C16.7955 4.83226 16.5315 4.99538 16.3886 5.10717C16.0458 5.37476 15.7819 5.80545 15.6665 6.2893C15.5602 6.731 15.5748 6.98025 17.0777 7.33947C17.3123 7.39629 17.8053 7.51176 17.9171 7.46594C17.9171 7.46594 18.0289 7.37613 18.1462 6.88311C18.3698 5.94657 17.9923 5.0192 17.3196 4.85792C17.2482 4.84143 17.1785 4.83226 17.1107 4.83226Z" fill="#808080"/>
                      <path d="M9.68653 8.10003C9.35114 8.10003 9.01389 8.05238 8.70599 7.90026C8.1305 7.61435 7.79693 7.04986 7.68879 6.17929C7.58982 5.38204 7.74927 4.59395 8.13781 3.95982C8.55385 3.28352 9.20448 2.84365 9.92659 2.75568C10.6432 2.66587 11.3855 2.93164 11.9555 3.48514C12.4888 4.00381 12.8352 4.72956 12.9342 5.52682C13.0716 6.62648 12.5694 7.21665 12.1222 7.51722C11.6182 7.85811 10.9695 7.97539 10.3995 8.04687C10.1704 8.07619 9.92846 8.10003 9.68287 8.10003H9.68653ZM10.2088 4.20173C10.1759 4.20173 10.1429 4.20357 10.108 4.20907C9.72865 4.25672 9.49224 4.55362 9.38777 4.7259C9.17517 5.07229 9.08718 5.53416 9.144 5.99602C9.18432 6.31858 9.26499 6.53851 9.35846 6.58616C9.53807 6.67597 10.0347 6.61366 10.2217 6.58983C10.5956 6.54401 11.0556 6.46888 11.303 6.30026C11.3891 6.24161 11.5358 6.14264 11.4808 5.70461C11.424 5.24275 11.2242 4.81388 10.9328 4.53164C10.8008 4.40334 10.5406 4.1999 10.2088 4.1999V4.20173Z" fill="#808080"/>
                      <path d="M4.47307 12.2714C3.619 12.2714 3.12602 11.7308 2.90609 11.4045C2.08684 10.1839 2.35989 8.55825 3.51637 7.78115C4.67284 7.00589 6.28202 7.36512 7.1031 8.58574C7.52281 9.21071 7.6291 9.81002 7.422 10.369C7.21123 10.939 6.72188 11.3221 6.31867 11.5933C5.57274 12.0937 4.96425 12.2714 4.47307 12.2714ZM4.87631 8.83499C4.6857 8.83499 4.49689 8.88815 4.33377 8.99628C3.84809 9.32251 3.75281 10.0355 4.12302 10.5853C4.22199 10.7337 4.45659 11.0765 5.50127 10.3745C5.91181 10.0996 6.01994 9.93281 6.04743 9.85767C6.08958 9.74221 5.9833 9.5461 5.88617 9.40131C5.64058 9.03659 5.25203 8.83316 4.87631 8.83316V8.83499Z" fill="#808080"/>
                      <path d="M22.6033 13.0027C21.9069 13.0027 21.2874 12.5867 20.8989 12.3264C20.2739 11.9049 18.3678 10.6256 19.6837 8.66823C20.7192 7.12687 22.6198 6.58254 23.9193 7.45677C25.2187 8.331 25.435 10.2939 24.3995 11.8353C23.8056 12.7205 23.1788 13.0027 22.6033 13.0027ZM22.5117 8.50146C21.9545 8.50146 21.3149 8.86618 20.9007 9.48382C20.591 9.94568 20.492 10.2847 21.7163 11.1077C22.6235 11.7161 22.7463 11.6666 23.1825 11.016C23.758 10.1601 23.7195 9.0861 23.1018 8.6719C22.9296 8.55643 22.7261 8.50146 22.5117 8.50146Z" fill="#808080"/>
                      <path d="M22.7727 42.5084H4.44509C4.24348 42.5084 4.05106 42.4259 3.91177 42.2793C3.77431 42.1327 3.70098 41.9348 3.71198 41.735C3.71931 41.603 4.44325 28.4933 4.44325 22.7146C4.44325 18.8547 3.80547 16.3274 3.36744 15.0444C2.50604 14.5423 1.74727 13.822 1.16079 12.9496C-0.642656 10.2646 -0.307292 6.8703 1.90669 5.3821C2.92021 4.70214 4.20499 4.51885 5.48976 4.84325C5.75001 2.29754 7.38668 0.307158 9.58417 0.0322431C11.2776 -0.180358 12.9088 0.670053 13.9828 2.2389C15.1979 1.03661 16.7686 0.521594 18.2715 0.882649C20.0181 1.30235 21.3157 2.84371 21.6914 4.85059C22.9909 4.5042 24.2701 4.68748 25.3075 5.38393C27.5215 6.87214 27.8568 10.2664 26.0534 12.9514C25.4669 13.8256 24.7082 14.5441 23.8468 15.0463C23.4069 16.3274 22.7691 18.8529 22.7691 22.7164C22.7691 28.4933 23.493 41.6049 23.5003 41.7368C23.5113 41.9384 23.4398 42.1345 23.3006 42.2812C23.1613 42.4278 22.9688 42.5103 22.7672 42.5103L22.7727 42.5084ZM5.21851 41.0422H21.9975C21.8417 38.1244 21.3047 27.7125 21.3047 22.7146C21.3047 18.3507 22.0928 15.5594 22.562 14.2838C22.6243 14.1134 22.7489 13.9723 22.912 13.888C23.6635 13.4958 24.3288 12.8891 24.8383 12.1322C26.1909 10.1198 26.035 7.6364 24.4919 6.60089C23.6415 6.0309 22.5052 5.99973 21.3725 6.5184C21.1508 6.62104 20.8923 6.60455 20.6816 6.47625C20.4708 6.34796 20.3425 6.12253 20.3333 5.87877C20.2673 4.07166 19.3015 2.63661 17.9288 2.30855C16.7063 2.0153 15.3757 2.63845 14.4611 3.93788C14.3108 4.15048 14.0616 4.2696 13.794 4.24577C13.5319 4.22194 13.3047 4.05884 13.1947 3.82058C12.4671 2.23524 11.1237 1.32069 9.76378 1.48747C8.0648 1.70007 6.81669 3.60981 6.92482 5.83845C6.93765 6.09504 6.81302 6.3388 6.60042 6.48359C6.38599 6.62655 6.11109 6.64669 5.88016 6.53673C4.73651 5.99973 3.58737 6.02174 2.72231 6.60089C1.17912 7.63824 1.02331 10.1198 2.37589 12.1322C2.8854 12.8891 3.55069 13.4976 4.30213 13.888C4.46341 13.9723 4.58802 14.1134 4.65217 14.2857C5.12136 15.5613 5.90947 18.3544 5.90947 22.7146C5.90947 27.7125 5.37246 38.1244 5.21667 41.0422H5.21851Z" fill="#808080"/>
                      <path d="M16.1762 19.7822C15.3551 19.7822 14.9995 19.5824 14.6495 19.3405C14.4314 19.1902 14.2261 19.0491 13.2438 19.0491C12.2614 19.0491 12.0561 19.1902 11.838 19.3405C11.488 19.5824 11.1324 19.7822 10.3113 19.7822C9.29598 19.7822 7.37891 18.8621 7.37891 15.3835C7.37891 12.134 10.5881 9.51868 13.2438 9.51868C15.8994 9.51868 19.1086 12.134 19.1086 15.3835C19.1086 18.8621 17.1915 19.7822 16.1762 19.7822ZM13.2438 17.5828C14.5743 17.5828 15.0454 17.8321 15.4816 18.1345C15.6612 18.2591 15.7436 18.316 16.1762 18.316C16.5849 18.316 17.6424 17.7881 17.6424 15.3835C17.6424 12.8323 15.0142 10.9849 13.2438 10.9849C11.4733 10.9849 8.84512 12.8323 8.84512 15.3835C8.84512 17.7881 9.90262 18.316 10.3113 18.316C10.742 18.316 10.8245 18.2591 11.006 18.1345C11.444 17.8339 11.9132 17.5828 13.2438 17.5828Z" fill="#808080"/>
                      <path d="M42.3818 8.93766C42.7557 8.93766 43.1369 8.84601 43.4741 8.76537C44.3722 8.5491 46.4725 8.04692 45.9704 5.94657C45.7761 5.13283 45.3325 4.42354 44.7222 3.94885C44.0734 3.44301 43.2835 3.25424 42.5486 3.43019C41.0787 3.78208 40.221 5.48471 40.637 7.22401C40.9669 8.60224 41.6633 8.93766 42.3818 8.93766ZM43.1002 4.83226C43.4155 4.83226 43.6794 4.99538 43.8224 5.10717C44.1651 5.37476 44.429 5.80545 44.5445 6.2893C44.6508 6.731 44.6361 6.98025 43.1332 7.33947C42.8986 7.39629 42.4056 7.51176 42.2938 7.46594C42.2938 7.46594 42.182 7.37613 42.0647 6.88311C41.8411 5.94657 42.2187 5.0192 42.8913 4.85792C42.9628 4.84143 43.0324 4.83226 43.1002 4.83226Z" fill="#808080"/>
                      <path d="M50.5283 8.10003C50.8637 8.10003 51.201 8.05238 51.5089 7.90026C52.0843 7.61435 52.4179 7.04986 52.526 6.17929C52.625 5.38204 52.4656 4.59395 52.077 3.95982C51.661 3.28352 51.0104 2.84365 50.2883 2.75568C49.5716 2.66587 48.8294 2.93164 48.2594 3.48514C47.726 4.00381 47.3796 4.72956 47.2807 5.52682C47.1432 6.62648 47.6454 7.21665 48.0926 7.51722C48.5966 7.85811 49.2454 7.97539 49.8154 8.04687C50.0445 8.07619 50.2864 8.10003 50.532 8.10003H50.5283ZM50.006 4.20173C50.039 4.20173 50.072 4.20357 50.1068 4.20907C50.4862 4.25672 50.7226 4.55362 50.8271 4.7259C51.0397 5.07229 51.1277 5.53416 51.0708 5.99602C51.0305 6.31858 50.9499 6.53851 50.8564 6.58616C50.6768 6.67597 50.1801 6.61366 49.9932 6.58983C49.6193 6.54401 49.1593 6.46888 48.9118 6.30026C48.8257 6.24161 48.6791 6.14264 48.7341 5.70461C48.7909 5.24275 48.9907 4.81388 49.2821 4.53164C49.414 4.40334 49.6743 4.1999 50.006 4.1999V4.20173Z" fill="#808080"/>
                      <path d="M55.7418 12.2714C56.5958 12.2714 57.0888 11.7308 57.3088 11.4045C58.128 10.1839 57.855 8.55825 56.6985 7.78115C55.542 7.00589 53.9328 7.36512 53.1117 8.58574C52.692 9.21071 52.5857 9.81002 52.7928 10.369C53.0036 10.939 53.493 11.3221 53.8962 11.5933C54.6421 12.0937 55.2506 12.2714 55.7418 12.2714ZM55.3385 8.83499C55.5291 8.83499 55.718 8.88815 55.8811 8.99628C56.3668 9.32251 56.462 10.0355 56.0918 10.5853C55.9929 10.7337 55.7583 11.0765 54.7136 10.3745C54.303 10.0996 54.1949 9.93281 54.1674 9.85767C54.1253 9.74221 54.2315 9.5461 54.3287 9.40131C54.5743 9.03659 54.9628 8.83316 55.3385 8.83316V8.83499Z" fill="#808080"/>
                      <path d="M37.6115 13.0027C38.308 13.0027 38.9274 12.5867 39.316 12.3264C39.941 11.9049 41.8471 10.6256 40.5311 8.66823C39.4956 7.12687 37.595 6.58254 36.2956 7.45677C34.9962 8.331 34.7799 10.2939 35.8154 11.8353C36.4092 12.7205 37.036 13.0027 37.6115 13.0027ZM37.7031 8.50146C38.2603 8.50146 38.8999 8.86618 39.3142 9.48382C39.6239 9.94568 39.7229 10.2847 38.4986 11.1077C37.5914 11.7161 37.4686 11.6666 37.0324 11.016C36.4569 10.1601 36.4954 9.0861 37.113 8.6719C37.2853 8.55643 37.4887 8.50146 37.7031 8.50146Z" fill="#808080"/>
                      <path d="M37.4421 42.5084H55.7698C55.9714 42.5084 56.1638 42.4259 56.3031 42.2793C56.4405 42.1327 56.5139 41.9348 56.5029 41.735C56.4955 41.603 55.7716 28.4933 55.7716 22.7146C55.7716 18.8547 56.4094 16.3274 56.8474 15.0444C57.7088 14.5423 58.4676 13.822 59.0541 12.9496C60.8575 10.2646 60.5221 6.8703 58.3082 5.3821C57.2946 4.70214 56.0099 4.51885 54.7251 4.84325C54.4648 2.29754 52.8282 0.307158 50.6307 0.0322431C48.9372 -0.180358 47.306 0.670053 46.232 2.2389C45.0169 1.03661 43.4463 0.521594 41.9434 0.882649C40.1968 1.30235 38.8991 2.84371 38.5234 4.85059C37.224 4.5042 35.9447 4.68748 34.9074 5.38393C32.6934 6.87214 32.358 10.2664 34.1615 12.9514C34.748 13.8256 35.5067 14.5441 36.3681 15.0463C36.8079 16.3274 37.4458 18.8529 37.4458 22.7164C37.4458 28.4933 36.7218 41.6049 36.7145 41.7368C36.7035 41.9384 36.775 42.1345 36.9143 42.2812C37.0536 42.4278 37.246 42.5103 37.4476 42.5103L37.4421 42.5084ZM54.9963 41.0422H38.2174C38.3731 38.1244 38.9101 27.7125 38.9101 22.7146C38.9101 18.3507 38.122 15.5594 37.6529 14.2838C37.5905 14.1134 37.4659 13.9723 37.3028 13.888C36.5514 13.4958 35.8861 12.8891 35.3766 12.1322C34.024 10.1198 34.1798 7.6364 35.723 6.60089C36.5734 6.0309 37.7097 5.99973 38.8423 6.5184C39.0641 6.62104 39.3225 6.60455 39.5333 6.47625C39.744 6.34796 39.8724 6.12253 39.8815 5.87877C39.9475 4.07166 40.9133 2.63661 42.2861 2.30855C43.5085 2.0153 44.8392 2.63845 45.7537 3.93788C45.904 4.15048 46.1532 4.2696 46.4208 4.24577C46.6829 4.22194 46.9102 4.05884 47.0201 3.82058C47.7477 2.23524 49.0912 1.32069 50.4511 1.48747C52.15 1.70007 53.3982 3.60981 53.29 5.83845C53.2772 6.09504 53.4018 6.3388 53.6144 6.48359C53.8289 6.62655 54.1038 6.64669 54.3347 6.53673C55.4783 5.99973 56.6275 6.02174 57.4925 6.60089C59.0357 7.63824 59.1915 10.1198 57.839 12.1322C57.3294 12.8891 56.6642 13.4976 55.9127 13.888C55.7514 13.9723 55.6268 14.1134 55.5627 14.2857C55.0935 15.5613 54.3054 18.3544 54.3054 22.7146C54.3054 27.7125 54.8424 38.1244 54.9982 41.0422H54.9963Z" fill="#808080"/>
                      <path d="M44.0387 19.7822C44.8597 19.7822 45.2153 19.5824 45.5654 19.3405C45.7835 19.1902 45.9887 19.0491 46.9711 19.0491C47.9534 19.0491 48.1587 19.1902 48.3768 19.3405C48.7269 19.5824 49.0824 19.7822 49.9035 19.7822C50.9189 19.7822 52.8359 18.8621 52.8359 15.3835C52.8359 12.134 49.6268 9.51868 46.9711 9.51868C44.3154 9.51868 41.1062 12.134 41.1062 15.3835C41.1062 18.8621 43.0233 19.7822 44.0387 19.7822ZM46.9711 17.5828C45.6405 17.5828 45.1695 17.8321 44.7333 18.1345C44.5537 18.2591 44.4712 18.316 44.0387 18.316C43.63 18.316 42.5724 17.7881 42.5724 15.3835C42.5724 12.8323 45.2006 10.9849 46.9711 10.9849C48.7415 10.9849 51.3697 12.8323 51.3697 15.3835C51.3697 17.7881 50.3122 18.316 49.9035 18.316C49.4728 18.316 49.3903 18.2591 49.2089 18.1345C48.7709 17.8339 48.3017 17.5828 46.9711 17.5828Z" fill="#808080"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Dots */}
      <div className="flex justify-center items-center mt-[20px] space-x-[6px] flex-wrap gap-y-1">
        {getVisibleDotIndices(carouselSlides.length, currentSlide).map((index) => {
          const isActive = index === currentSlide;
          const isAdjacent = Math.abs(index - currentSlide) === 1;
          
          return (
            <button
              key={index}
              type="button"
              aria-label={`Slide ${index + 1}`}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-200 ${
                isActive 
                  ? 'bg-black text-white px-[8px] py-[6px] letter-spacing-[0.12px] rounded-full text-[12px] font-medium' 
                  : isAdjacent
                  ? 'w-[6.5px] h-[6.5px] rounded-full bg-gray-300'
                  : 'w-[4px] h-[4px] rounded-full bg-gray-300'
              }`}
            >
              {isActive ? `${currentSlide + 1}/${carouselSlides.length}` : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SpotlightCarousel; 